import { useMemo, useRef, useState, useLayoutEffect } from "react";
import { formatINR } from "../lib/format";

// A hand-crafted SVG line chart with a self-drawing glowing stroke, gradient
// area fill and a holographic hover tooltip.  Kept dependency-free so the
// animations (line-draw, glow, pulse) can be tightly synchronised with the
// rest of the Price Pulse terminal.
export const PricePulseChart = ({ series, color, unit, brand, height = 180, targets = [] }) => {
  const wrapRef = useRef(null);
  const [w, setW] = useState(360);
  const [hover, setHover] = useState(null); // { x, y, i, price, date }

  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.max(200, e.contentRect.width));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const { path, area, points, ymin, ymax, xstep, targetLines } = useMemo(() => {
    if (!series || series.length === 0) return { path: "", area: "", points: [], ymin: 0, ymax: 1, xstep: 1, targetLines: [] };
    const prices = series.map((p) => p.price);
    let ymin = Math.min(...prices);
    let ymax = Math.max(...prices);
    // Expand the y-range so target lines beyond the current series stay visible
    for (const t of (targets || [])) {
      if (typeof t.target_price === "number") {
        ymin = Math.min(ymin, t.target_price);
        ymax = Math.max(ymax, t.target_price);
      }
    }
    // Add a small vertical pad so lines don't touch chart edges
    const spread = Math.max(1, ymax - ymin);
    ymin -= spread * 0.06;
    ymax += spread * 0.06;
    const range = Math.max(1, ymax - ymin);
    const pad = 8;
    const H = height - pad * 2;
    const step = series.length > 1 ? (w - pad * 2) / (series.length - 1) : 0;
    const pts = series.map((p, i) => {
      const x = pad + i * step;
      const y = pad + H - ((p.price - ymin) / range) * H;
      return { x, y, price: p.price, date: p.date };
    });
    const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ");
    const area = `${path} L ${pts[pts.length - 1].x} ${height - pad} L ${pts[0].x} ${height - pad} Z`;
    const targetLines = (targets || []).map((t) => ({
      ...t,
      y: pad + H - ((t.target_price - ymin) / range) * H,
    }));
    return { path, area, points: pts, ymin, ymax, xstep: step, targetLines };
  }, [series, w, height, targets]);

  const gid = `grad-${color.replace("#", "")}`;
  const glowId = `glow-${color.replace("#", "")}`;
  // dash length approx = polyline length; over-estimate so it fully hides on load
  const dash = Math.max(600, w * 2);

  const onMove = (e) => {
    if (!points.length) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let idx = 0;
    let best = Infinity;
    for (let i = 0; i < points.length; i++) {
      const d = Math.abs(points[i].x - x);
      if (d < best) { best = d; idx = i; }
    }
    setHover({ ...points[idx], i: idx });
  };

  const fmtDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y.slice(2)}`;
  };

  return (
    <div ref={wrapRef} className="relative" style={{ height }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg width={w} height={height} className="block" data-testid="pulse-chart-svg">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.42" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
          <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* faint horizontal grid */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={w} y1={height * f} y2={height * f} stroke="rgba(255,255,255,0.05)" strokeDasharray="2 4" />
        ))}

        {/* area gradient */}
        <path d={area} fill={`url(#${gid})`} opacity="0.9" />

        {/* main glowing line (self-drawing) */}
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter={`url(#${glowId})`}
          className="line-draw"
          style={{ "--dash": dash }}
        />

        {/* endpoint pulse dot */}
        {points.length > 0 && (
          <g>
            <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="6" fill={color} opacity="0.25">
              <animate attributeName="r" values="4;10;4" dur="1.8s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.5;0;0.5" dur="1.8s" repeatCount="indefinite" />
            </circle>
            <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3" fill={color} />
          </g>
        )}

        {/* Partner alert target lines — stacked, colour-coded by status */}
        {targetLines.map((t) => {
          const active = t.status === "active";
          const stroke = active ? "#2FB999" : "#8A8A82";
          const fill = active ? "#2FB999" : "#8A8A82";
          return (
            <g key={t.id} data-testid={`target-line-${t.id}`}>
              <line x1="0" x2={w} y1={t.y} y2={t.y} stroke={stroke} strokeOpacity={active ? 0.55 : 0.3} strokeDasharray="4 4" strokeWidth="1" />
              <g transform={`translate(${w - 6}, ${t.y})`}>
                <polygon points="0,-4 -8,0 0,4" fill={fill} opacity={active ? 0.9 : 0.55} />
              </g>
              <text x={4} y={t.y - 3} fontSize="9" fontFamily="Cabinet Grotesk, Manrope, sans-serif" fill={stroke} opacity={active ? 0.9 : 0.55} style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {active ? "▲ target" : "hit"} ₹{Math.round(t.target_price).toLocaleString("en-IN")}
              </text>
            </g>
          );
        })}

        {/* hover crosshair */}
        {hover && (
          <g>
            <line x1={hover.x} x2={hover.x} y1="0" y2={height} stroke={color} strokeOpacity="0.35" strokeDasharray="3 3" />
            <circle cx={hover.x} cy={hover.y} r="5" fill={color} stroke="#0a0a0a" strokeWidth="2" />
          </g>
        )}
      </svg>

      {/* Holographic glass tooltip */}
      {hover && (
        <div
          className="holo-tip absolute pointer-events-none rounded-sm px-3 py-2 min-w-[140px]"
          style={{
            left: Math.min(Math.max(hover.x + 12, 4), w - 160),
            top: Math.max(hover.y - 56, 4),
            borderColor: `${color}66`,
          }}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full pulse-dot" style={{ backgroundColor: color, color }} />
            <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] truncate" style={{ color }}>{brand || "Live"}</span>
          </div>
          <div className="font-serif text-base leading-tight" style={{ color: "#F5F5F0" }}>
            {formatINR(Math.round(hover.price))}
          </div>
          <div className="font-body text-[9px] text-[#8A8A82] mt-0.5">{fmtDate(hover.date)} · {unit}</div>
        </div>
      )}
    </div>
  );
};
